import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import data from "../data/imageData.json";
import "./styles/AlbumPage.css";
import Navbar from "../components/Navbar";
import ResponsiveImage from "../components/ResponsiveImage";
import NotFoundPage from "./NotFoundPage";
import Footer from "../components/Footer";

const AlbumPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const album = data.albums.find((a) => a.slug === slug);

  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const isOpen = lightboxIdx !== null;

  const close = useCallback(() => setLightboxIdx(null), []);
  const next = useCallback(() => {
    if (!album || lightboxIdx === null) return;
    setLightboxIdx((lightboxIdx + 1) % album.images.length);
  }, [album, lightboxIdx]);
  const prev = useCallback(() => {
    if (!album || lightboxIdx === null) return;
    setLightboxIdx((lightboxIdx - 1 + album.images.length) % album.images.length);
  }, [album, lightboxIdx]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close, next, prev]);

  const [touchX, setTouchX] = useState<number | null>(null);
  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    setTouchX(e.touches[0].clientX);
  };
  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (dx > 60) prev();
    if (dx < -60) next();
    setTouchX(null);
  };

  if (!album) {
    return <NotFoundPage />;
  }

  return (
    <div className="albumPage">
      <div className="albumPageHeader">
        <ResponsiveImage src={album.cover} alt="" className="heroImage" priority />
        <Navbar />
      </div>

      <main className="albumPageContent">
        <h1 className="albumTitle">{album.name}</h1>

        <div className="albumGallery">
          {album.images.map((img, idx) => (
            <button
              key={img.id}
              className="albumThumbBtn"
              onClick={() => setLightboxIdx(idx)}
              aria-label={`Deschide imaginea ${idx + 1}`}
            >
              <ResponsiveImage
                className="albumImage"
                src={img.url}
                alt={img.alt}
                priority={idx === 0}
                sizes="(max-width: 600px) calc(100vw - 48px), (max-width: 900px) 50vw, (max-width: 1200px) 50vw, 410px"
              />
            </button>
          ))}
        </div>

        <div className="albumBackLink">
          <Link to="/portfolio">← Înapoi la toate albumele</Link>
        </div>
      </main>
      <Footer />

      {isOpen && (
        <div
          className="lightboxOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Fotografii: ${album.name}`}
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="lightboxInner" onClick={(e) => e.stopPropagation()}>
            <button className="lightboxClose" onClick={close} aria-label="Închide">×</button>
            <button className="lightboxPrev" onClick={prev} aria-label="Anterior">‹</button>
            <button className="lightboxNext" onClick={next} aria-label="Următor">›</button>
            <ResponsiveImage
              className="lightboxImage"
              src={album.images[lightboxIdx!].url}
              alt={album.images[lightboxIdx!].alt}
              loading="eager"
              sizes="95vw"
            />
            <div className="lightboxCaption">
              {lightboxIdx! + 1}/{album.images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlbumPage;
